/**
 * Utility to calculate CME Futures market status and clean labels
 * Session: Opens Sunday 6:00 PM ET, Closes Friday 5:00 PM ET
 * Daily Halts: Mon-Thu 5:00 PM - 6:00 PM ET
 */

export function getMarketState(now = new Date()) {
  // Convert current time to a reliable Eastern Time string profile
  const etString = now.toLocaleString("en-US", { timeZone: "America/New_York" });
  const etDate = new Date(etString);

  const day = etDate.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday
  const hours = etDate.getHours();
  const minutes = etDate.getMinutes();
  const timeAsMinutes = hours * 60 + minutes;

  const fivePM = 17 * 60; // 17:00
  const sixPM = 18 * 60;  // 18:00

  let state = "open";
  let lastClose = null;
  let nextOpen = null;

  // 1. Weekend Logic (Friday 5PM -> Sunday 6PM)
  if ((day === 5 && timeAsMinutes >= fivePM) || day === 6 || (day === 0 && timeAsMinutes < sixPM)) {
    state = "closed_weekend";
    
    // Calculate the preceding Friday at 5 PM ET
    const daysSinceFriday = day === 6 ? 1 : day === 0 ? 2 : 0;
    lastClose = new Date(etDate);
    lastClose.setDate(etDate.getDate() - daysSinceFriday);
    lastClose.setHours(17, 0, 0, 0);

    // Calculate incoming Sunday at 6 PM ET
    const daysToSunday = day === 5 ? 2 : day === 6 ? 1 : 0;
    nextOpen = new Date(etDate);
    nextOpen.setDate(etDate.getDate() + daysToSunday);
    nextOpen.setHours(18, 0, 0, 0);
  } 
  // 2. Weekday Daily Maintenance Halt Logic (Mon-Thu 5PM -> 6PM)
  else if (timeAsMinutes >= fivePM && timeAsMinutes < sixPM && day !== 5 && day !== 6) {
    state = "closed_daily";
    
    lastClose = new Date(etDate);
    lastClose.setHours(17, 0, 0, 0);

    nextOpen = new Date(etDate);
    nextOpen.setHours(18, 0, 0, 0);
  }

  // 3. Upgrade to Pre-Open window check (within 12 hours of an upcoming open)
  if ((state === "closed_weekend" || state === "closed_daily") && nextOpen) {
    // Convert target ET calculations safely back to absolute system runtime MS
    const msToOpen = nextOpen.getTime() - etDate.getTime();
    if (msToOpen > 0 && msToOpen <= 12 * 60 * 60 * 1000) {
      state = "pre_open";
    }
  }

  return { state, lastClose, nextOpen };
}

/**
 * Builds the customized string for your dashboard metadata anchor
 */
// The sentiment-analyzer Edge Function refreshes this data every 30 minutes.
// Past this many hours since the last update, something upstream is broken
// (missing/renamed secret, API outage, cron failure, etc.) rather than the
// widget just catching a normal refresh cycle — so we say so explicitly
// instead of falling through to a bare clock time that looks current.
const STALE_THRESHOLD_HOURS = 3;

export function getContextualTimestampLabel(updatedAtIso, marketStateObj) {
  if (!updatedAtIso) return "Never";
  const updatedDate = new Date(updatedAtIso);
  if (isNaN(updatedDate.getTime())) return "Never";

  const ageMs = Date.now() - updatedDate.getTime();
  const ageHours = ageMs / (1000 * 60 * 60);

  // Safety net: if the data is older than a few refresh cycles should ever
  // allow, always say so plainly — regardless of market state — so a silent
  // upstream failure (like a missing API key) can't hide behind a
  // same-day-looking clock time or a market-closed message.
  if (ageHours >= STALE_THRESHOLD_HOURS) {
    const dateStr = updatedDate.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
    return `⚠ Stale — last updated ${dateStr}`;
  }

  const { state, lastClose, nextOpen } = marketStateObj;

  // Context State 1: Market is actively running
  if (state === "open") {
    const diff = Math.floor(ageMs / 1000);
    if (diff < 60) return "Updated just now";
    if (diff < 3600) return `Updated ${Math.floor(diff / 60)}m ago`;
    return `Updated ${Math.floor(diff / 3600)}h ago`;
  }

  // Options configuration for uniform time output formatting
  const timeFormatOptions = { weekday: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" };

  // Context State 2: Within 12 hours of general resume sequence
  if (state === "pre_open" && nextOpen) {
    const etString = new Date().toLocaleString("en-US", { timeZone: "America/New_York" });
    const msToOpen = nextOpen.getTime() - new Date(etString).getTime();
    
    const hoursLeft = Math.floor(msToOpen / (1000 * 60 * 60));
    const minsLeft = Math.floor((msToOpen % (1000 * 60 * 60)) / (1000 * 60));
    
    return `Markets reopen in ${hoursLeft}h ${minsLeft}m`;
  }

  // Context State 3: General Maintenance or Weekend Deep Freeze
  const closeStr = lastClose ? lastClose.toLocaleTimeString("en-US", timeFormatOptions) : "Fri 5:00 PM EST";
  const openStr = nextOpen ? nextOpen.toLocaleTimeString("en-US", timeFormatOptions) : "Sun 6:00 PM EST";
  
  return `Market closed — as of ${closeStr}. Will Reopen at: ${openStr}`;
}